import create from 'zustand';
import {devtools} from 'zustand/middleware';
import {getDateInCurrentTimezone} from '../../../utils/date';
import produce from 'immer';
import {PurchaseRequestController} from '../../../controllers/PurchaseRequestController';
import {isLabelValueEmpty, isLabelValueIdentical} from '../../../utils/checkObjects';
import {isAddressIdentical} from '../../../utils/address';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import {AccountDetailsByMaterial} from '../../../models/financial_infos/AccountDetailsByMaterial';
import {Account} from '../../../models/financial_infos/Account';
import {OneTimeVendor} from '../../../models/OneTimeVendor';
import {HeaderDetails} from '../../../models/HeaderDetails';
import {POSendingOptions} from '../../../models/POSendingOptions';
import {FinancialInfo} from '../../../models/FinancialInfo';
import {Material} from '../../../models/Material';
import {AttachmentsInfo} from '../../../models/AttachmentsInfo';
import {Attachment} from '../../../models/attachments_info/Attachment';
import {MaterialController} from '../../../controllers/MaterialController';
import {NBSValidation} from '../../../models/NBSValidation';
import {SBValidation} from '../../../models/SBValidation';
// eslint-disable-next-line no-unused-vars
import {BrickContext} from 'olympe';
import {WaysOfBuying} from '../../../models/WaysOfBuying';
import {deleteEmailsFromList, formatEmailList, mergeEmailsList} from '../../../utils/email';
import {complianceIndicatorOptions} from '../../_helpers';
import {getTotalValueOfConditions} from '../../../utils/transportCostConditions.js';
import {SOURCES, TRANSPORT_OPTIONS_ENUM} from '../../../utils/constants';
import {determinePRSource} from "../../../utils/sourceMappings";

// This type is defined to have the auto-completion
/**
 * @typedef {
 * {incoterms: LabelValue,
 * defaultRequestedDeliveryDate: Date,
 * setVmdSupplierAddresses: (function(string[]): void),
 * areThereMultipleOrderingAddresses: boolean,
 * setP2pSupplierAddresses: (function(string[]): void),
 * setInternalComment: (function(string): void),
 * getStatus: function(): string,
 * supplierLeadTime: number,
 * vendorCode: string,
 * EPRId: string,
 * internalComment: string,
 * setCreationDate: function(Date): void,
 * pslSupplier: boolean,
 * setSendToSupplier: function(boolean): void,
 * areAccountSplitByMaterial: boolean,
 * setSendToSpender: function(boolean): void,
 * requester: {label: string, value: string, email: string},
 * setRequester: function({label: string, value: string, email: string}): void,
 * setAccountDetailsByMaterial: (function(AccountDetailsByMaterial[], number): void),
 * setEditableSupplier: function(boolean): void,
 * hidePrice: boolean,
 * setHidePrice: function(boolean): void,
 * createAccount: function(number): void,
 * prNumber: string,
 * competitiveBiddingJustificationAttachments: Attachment[],
 * setCbFormID: function(string): void,
 * setPslSupplier: function(boolean): void,
 * OTVAddress: DeliveryAddress,
 * areThereMultipleInvoicePartners: boolean,
 * setVendorName: function(string): void,
 * setExternalComment: function(string): void,
 * defaultStorageLocation: LabelValue,
 * sendToOther: boolean,
 * setPrNumber: function(string): void,
 * selectedException: string,
 * setSendToOther: function(boolean): void,
 * supplierStatus: string,
 * pslId: string,
 * externalComment: string,
 * vendorContactEmails: string[],
 * getTotalPrice: function(): number,
 * getTotalPriceWithoutCurrencyExchange: function(): number,
 * multipleDeliveryAddress: (function(): Material),
 * setSelectedException: function(string): void,
 * setValidityStart: function(Date): void,
 * setDefaultRequestedDeliveryDate: function(Date): void,
 * getTotalMaterialPrice: (function(number): number),
 * getTotalMaterialPriceWithoutCurrencyExchange: (function(number): number),
 * setSpender: function({label: string, value: string, email: string}): void,
 * hasException: boolean,
 * aribaID: string,
 * accountDetailsByMaterial: {material_id: number, accounts: Account[]}[],
 * setP2pSupplier: function(boolean): void,
 * setP2pOther: function(boolean): void,
 * otherAddress: string,
 * setPaymentTerms: function(LabelValue): void,
 * setAreThereMultipleInvoicePartners: function(boolean): void,
 * vmdSupplier: boolean,
 * setAccounts: function(Account[], number): void,
 * composing: boolean,
 * getTotalQuantity: function(): number,
 * validityEnd: Date,
 * invoicePartner: LabelValue,
 * setMainAttachmentTopic: function(string, string): void,
 * setMainAttachments: function(Attachment[]): void,
 * setNewPaymentTerms: function(LabelValue): void,
 * sendToSpender: boolean,
 * setPurchaseOrganization: function(LabelValue): void,
 * setShowCBJustification: function(boolean): void,
 * pslCompanyCodes: LabelValue[],
 * plants: LabelValue[],
 * orderingAddress: LabelValue,
 * editableSupplier: boolean,
 * sendToSupplier: boolean,
 * OTVName: string,
 * multipleStorageLocations: function(): Material,
 * setSendToRequester: function(boolean): void,
 * accounts:
 *  {accountStatus: string,
 *  consumptionLevel: string,
 *  itemCategory: LabelValue,
 *  costCenter: LabelValue,
 *  accountType: LabelValue,
 *  glNumber: LabelValue,
 *  wbs: LabelValue,
 *  allocateFor: number,
 *  profitCenter: LabelValue,
 *  allocationType: string}[],
 * businessUnit: LabelValue,
 * deleteCBAttachment: function(number): void,
 * deleteAllCBAttachment: function(number): void,
 * setAreAccountSplitByMaterial: function(boolean): void,
 * deleteMaterial: function(number): void,
 * createMainAttachment: function(Attachment): void,
 * setOtherAddress: function(string): void,
 * source: string,
 * sourceFromApiKey: string,
 * setDefaultPlant: function(LabelValue, DeliveryAddress): void,
 * deleteAccountDetailsByMaterial: function(number, number): void,
 * showCBJustification: boolean,
 * setOrderingAddress: function(LabelValue): void,
 * supplierAddresses: string[],
 * setSendToBuyer: function(boolean): void,
 * setSupplierStatus: function(string): void,
 * setPslId: function(string): void,
 * setWayOfBuying: function(LabelValue): void,
 * setHasException: function(boolean): void,
 * setPurchasingGroup: function(LabelValue): void,
 * setSupplierAddresses: function(string[]): void,
 * deleteAccount: function(number): void,
 * setInvoicePartner: function(LabelValue): void,
 * paymentTerms: LabelValue,
 * wayOfBuying: WaysOfBuying,
 * companyCode: LabelValue,
 * vmdSupplierAddresses: string[],
 * setOTVAddress: function(DeliveryAddress): void,
 * setCBAttachmentTopic: (function(string, string): void),
 * deleteMainAttachment: function(number): void,
 * purchasingGroup: LabelValue,
 * validityStart: Date,
 * vendorName: string,
 * creationDate: Date,
 * setCompetitiveBiddingJustificationAttachments: function(Attachment[]): void,
 * setCompanyCode: function(LabelValue): void,
 * setMaterials: function(Material[], number): void,
 * updateAllMaterials: function(Material[]): void,
 * sendToBuyer: boolean,
 * materials: Material[],
 * multipleRequestedDeliveryDates: function(): Material,
 * p2pSupplierAddresses: string[],
 * setSupplierLeadTime: function(number): void,
 * getPurchaseRequest: function(): PurchaseRequest,
 * setSource: function(string): void,
 * setBusinessUnit: function(LabelValue): void,
 * spender: {label: string, value: string, email: string},
 * purchaseOrganization: LabelValue,
 * setDefaultDeliveryAddress: function(DeliveryAddress): void,
 * defaultDeliveryAddress: DeliveryAddress,
 * setAribaID: function(string): void,
 * setPslCompanyCodes: function(LabelValue[]): void,
 * setPlants: function(LabelValue[]): void,
 * defaultPlant: LabelValue,
 * setOTVName: function(string): void,
 * mainAttachments: Attachment[],
 * setAllAccounts: function(Account[]): void,
 * p2pSupplier: boolean,
 * p2pOther: boolean,
 * setOTVPhoneNumber: function(string): void,
 * newPaymentTerms: LabelValue,
 * setDefaultStorageLocation: function(LabelValue, DeliveryAddress): void,
 * setProjectTitle: function(string): void,
 * getTotalMaterialQuantity: function(number): number,
 * projectTitle: string,
 * setComposing: function(boolean): void,
 * cbFormID: string,
 * setVendorCode: function(string): void,
 * setVmdSupplier: function(boolean): void,
 * OTVPhoneNumber: string,
 * setValidityEnd: function(Date): void,
 * setAreThereMultipleOrderingAddresses: function(boolean): void,
 * isTotalGreaterThanThreshold: boolean,
 * setTotalGreaterThanThreshold: function(boolean): void,
 * sendToRequester: boolean,
 * multiplePlants: function(): Material,
 * createCBAttachment: function(Attachment): void,
 * coversheet: string,
 * setCoversheet: function(string): void,
 * deleteConditionTypeForAllMaterials: function(string,string[])
 * addConditionTypeForAllMaterials: function(object,string[])
 * addConditionTransportCost: function(function(),array, array, number, array)
 * removeTransportCostsConditionsForSelected: function(array, function(boolean)): void
 * removeTransportCostsConditions: function(function()): void
 * addTransportCosts: function(function(), number): void,
 * duplicateMaterial: function(number): void,
 * supplierType: string,
 * setSupplierType: function(string): void,
 * userDateFormat: function(string): void,
 * userCurrencySeparator: function(string): void,
 * getTotalConditionsPrice: function(): number,
 * getTotalConditionPriceForMaterial: function(Material): number,
 * nonPslJustification : string;
 * setNonPslJustification: function(string):void
 * } PRStore
 */

/**
 * This is the function that creates a store for each purchase request, containing all the information used in the app
 * @param {PurchaseRequest} pr
 * @param {BrickContext} $
 * @return {*}
 */
export function createPRStore(pr, $) {
    return create(devtools(
        /**
         * @param {function(*)} set
         * @param {function(): PRStore} get
         * @return {PRStore}
         */
        (set, get) => ({
            vendorName: pr.vendorName,
            setVendorName: (newVendorName) => set(produce((state) => {
                state.vendorName = newVendorName;
            })),

            vendorCode: pr.vendorCode,
            setVendorCode: (newVendorCode) => set({vendorCode: newVendorCode}),

            // OTV = One Time Vendor, used in the Select Supplier Popup
            OTVName: pr.oneTimeVendor.supplierName,
            setOTVName: (newOTVName) => set({OTVName: newOTVName}),

            OTVAddress: pr.oneTimeVendor.address,
            setOTVAddress: (newOTVAddress) => set({OTVAddress: newOTVAddress}),

            OTVPhoneNumber: pr.oneTimeVendor.phoneNumber,
            setOTVPhoneNumber: (newOTVPhoneNumber) => set({OTVPhoneNumber: newOTVPhoneNumber}),

            operationalBuyers: pr.nbsValidation.operationalBuyers,
            setOperationalBuyers: (newOBs) => set({operationalBuyers: newOBs}),

            operationalBuyersInDL: pr.nbsValidation.operationalBuyersInDL,
            setOperationalBuyersInDL: (newDLs) => set({operationalBuyersInDL: newDLs}),

            denialInfo: pr.nbsValidation.denialInfo,
            setDenialInfo: (newDenialInfo) => set({denialInfo: newDenialInfo}),

            rejectionMsg: pr.rejectionMsg,
            setRejectionMsg: (newMsg) => set({rejectionMsg: newMsg}),

            strategicBuyers: pr.sbValidation.strategicBuyers || [],
            setStrategicBuyers: (newSBs) => set({strategicBuyers: newSBs}),

            sbDenialInfo: pr.sbValidation.denialInfo,
            setSBDenialInfo: (newDenialInfo) => set({sbDenialInfo: newDenialInfo}),

            sbApprover: pr.sbValidation.sbApprover,
            setSBApprover: (newApprover) => set({sbApprover: newApprover}),

            obApprover: pr.nbsValidation.obApprover,
            setOBApprover: (newApprover) => set({obApprover: newApprover}),

            creationDate: getDateInCurrentTimezone(pr.creationDate),
            setCreationDate: (newCreationDate) => set({creationDate: newCreationDate}),

            submissionDate: pr.submissionDate ? getDateInCurrentTimezone(pr.submissionDate) : '',
            setSubmissionDate: (newSubmissionDate) => set({submissionDate: newSubmissionDate}),

            EPRId: pr.EPRId,

            prNumber: pr.prNumber,
            setPrNumber: (newPrNumber) => set({prNumber: newPrNumber}),

            projectTitle: pr.projectTitle,
            setProjectTitle: (newProjectTitle) => set(produce((state) => {
                state.projectTitle = newProjectTitle;
                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);

                state.internalComment = updatedComment;
                return state;
            })),
            wayOfBuying: pr.wayOfBuying.getWayOfBuying(),
            setWayOfBuying: (newWayOfBuying, userDefaults) => set(produce((state) => {
                state.wayOfBuying = {
                    ...state.wayOfBuying,
                    currentValue: newWayOfBuying,
                };

                if (newWayOfBuying.value === 'FO') {
                    state.materials.map((material, index) => {
                        material.quantity = 1;
                    });
                    state.accounts.map((account, index) => {
                        account.itemCategory = {label: 'B - Limit', value: 'B'};
                    });
                }
                if (userDefaults) {
                    state.hidePrice = (userDefaults.hidePrice === true) && newWayOfBuying.value === 'FO';
                }
                return state;
            })),

            spender: pr.spender,
            setSpender: (newSpender) => set(produce((state) => {
                state.spender = newSpender;

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;

                return state;
            })),

            requester: pr.requester,
            setRequester: (newRequester) => set(produce((state) => {
                state.requester = newRequester;

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;

                return state;
            })),

            userIdToCallSAPAPIs: pr.spender.value,
            setUserIdToCallSAPAPIs: (newValue) => set({userIdToCallSAPAPIs: newValue}),

            internalComment: pr.internalComment,
            setInternalComment: (newInternalComment) => set({internalComment: newInternalComment}),

            externalComment: pr.externalComment,
            setExternalComment: (newExternalComment) => set({externalComment: newExternalComment}),

            status: pr.status,
            setStatus: (newStatus) => set({status: newStatus}),

            // This getStatus method is used to determine the status of the request.
            getStatus: () => get().status,

            // This property indicate whether the request was created from the creation API, or from the "Describe your needs" (DYN) popup
            source: pr.source,
            setSource: (newSource) => set({source: newSource}),

            sourceFromApiKey: pr.sourceFromApiKey,

            sourceToDisplay: pr.sourceToDisplay,
            setSourceToDisplay: (value) => set({sourceToDisplay: value}),

            prSource: pr.prSource,
            setPrSource: (value) => set({prSource: value}),

            nonPslJustification: pr.nonPslJustification || '',
            setNonPslJustification: (newJustification) => set(produce((state) => {
                state.nonPslJustification = newJustification;
            })),

            contractID: pr.contractID,

            editableSupplier: pr.editableSupplier,
            setEditableSupplier: (newEditableSupplier) => set({editableSupplier: newEditableSupplier}),

            vendorContactEmails: pr.vendorContactEmails,

            // This property is used to determine the requested delivery date, by adding it to today's date
            supplierLeadTime: pr.supplierLeadTime,
            setSupplierLeadTime: (newSupplierLeadTime) => set({supplierLeadTime: newSupplierLeadTime}),

            pslCompanyCodes: pr.headerDetails.pslCompanyCodes,
            setPslCompanyCodes: (newPslCompanyCodes) => set({pslCompanyCodes: newPslCompanyCodes}),

            plants: pr.headerDetails.plants,
            setPlants: (newPlants) => set({plants: newPlants}),

            companyCode: pr.headerDetails.companyCode,
            setCompanyCode: (newCompanyCode) => set({companyCode: newCompanyCode}),

            purchaseOrganization: pr.headerDetails.purchaseOrganization,
            setPurchaseOrganization: (newPurchaseOrganization) => set({purchaseOrganization: newPurchaseOrganization}),

            purchasingGroup: pr.headerDetails.purchasingGroup,
            setPurchasingGroup: (newPurchasingGroup) => set({purchasingGroup: newPurchasingGroup}),

            businessUnit: pr.headerDetails.businessUnit,
            setBusinessUnit: (newBusinessUnit) => set({businessUnit: newBusinessUnit}),

            defaultPlant: pr.headerDetails.defaultPlant,
            setDefaultPlant: (newPlant, plantAddress) => set(produce((state) => {
                state.defaultPlant = newPlant;
                state.defaultDeliveryAddress = plantAddress;

                state.materials.forEach((material) => {
                    material.plant = newPlant;
                    material.deliveryAddress = plantAddress;
                });
                return state;
            })),

            defaultStorageLocation: pr.headerDetails.defaultStorageLocation,
            setDefaultStorageLocation: (newStorageLocation, storageLocationAddress) => set(produce((state) => {
                const useStorageLocationAddress = !!storageLocationAddress;
                state.defaultStorageLocation = newStorageLocation;
                if (useStorageLocationAddress) {
                    state.defaultDeliveryAddress = storageLocationAddress;
                }
                state.materials.forEach((material) => {
                    material.storageLocation = newStorageLocation;
                    if (useStorageLocationAddress) {
                        material.deliveryAddress = storageLocationAddress;
                    }
                });
                return state;
            })),

            defaultDeliveryAddress: pr.headerDetails.defaultDeliveryAddress,
            setDefaultDeliveryAddress: (newDeliveryAddress) => set(produce((state) => {
                state.defaultDeliveryAddress = newDeliveryAddress;
                state.materials.forEach((material) => {
                    material.deliveryAddress = newDeliveryAddress;
                });
                return state;
            })),

            defaultRequestedDeliveryDate: getDateInCurrentTimezone(pr.headerDetails.defaultRequestedDeliveryDate),
            setDefaultRequestedDeliveryDate: (newDate) => set(produce((state) => {
                // The newDate parameter is a string representing a date without time (e.g. 2022-08-25). So we have to
                // manually add the timezone to keep the right day information
                // const newDateInCurrentTimezone = addCurrentTimezoneToDate(new Date(newDate));
                const newDateInCurrentTimezone = newDate;
                state.defaultRequestedDeliveryDate = newDateInCurrentTimezone;
                state.materials.forEach((material) => {
                    material.requestedDeliveryDate = newDateInCurrentTimezone;
                });
                return state;
            })),
            multiplePlants: () => get().materials.find((material) => material.plant.value !== get().defaultPlant.value),
            multipleStorageLocations: () => get().materials.find((material) => material.storageLocation.value !== get().defaultStorageLocation.value),
            multipleDeliveryAddress: () => get().materials.find((material) => !isAddressIdentical(material.deliveryAddress, get().defaultDeliveryAddress)),
            multipleRequestedDeliveryDates: () => get().materials.find((material) => material.requestedDeliveryDate.toString() !== get().defaultRequestedDeliveryDate.toString()),

            // Validity start and end replace the requested delivery date is the chosen way of buying is Framework Requisition
            validityStart: getDateInCurrentTimezone(pr.headerDetails.validityStart),
            setValidityStart: (newValidityStart) => set({validityStart: newValidityStart}),

            validityEnd: getDateInCurrentTimezone(pr.headerDetails.validityEnd),
            setValidityEnd: (newValidityEnd) => set(produce((state) => {
                state.validityEnd = newValidityEnd;
                state.materials.forEach((material) => {
                    material.requestedDeliveryDate = newValidityEnd;
                });
                return state;
            })),

            paymentTerms: pr.headerDetails.paymentTerms,
            setPaymentTerms: (newPaymentTerms) => set({paymentTerms: newPaymentTerms}),

            newPaymentTerms: pr.headerDetails.newPaymentTerms,
            setNewPaymentTerms: (newNewPaymentTerms) => set({newPaymentTerms: newNewPaymentTerms}),

            orderingAddress: pr.headerDetails.orderingAddress,
            setOrderingAddress: (newOrderingAddress) => set({orderingAddress: newOrderingAddress}),
            areThereMultipleOrderingAddresses: false,
            setAreThereMultipleOrderingAddresses: (thereAre) => set({areThereMultipleOrderingAddresses: thereAre}),

            invoicePartner: pr.headerDetails.invoicePartner,
            setInvoicePartner: (newInvoicePartner) => set({invoicePartner: newInvoicePartner}),
            areThereMultipleInvoicePartners: false,
            setAreThereMultipleInvoicePartners: (thereAre) => set({areThereMultipleInvoicePartners: thereAre}),

            incoterms: pr.headerDetails.incoterms,
            setIncoterms: (newIncoterms) => set({incoterms: newIncoterms}),

            supplierStatus: pr.headerDetails.supplierStatus,
            setSupplierStatus: (newSupplierStatus) => set({supplierStatus: newSupplierStatus}),

            updateInternalComment: () => set(produce((state) => {
                state.internalComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);

                return state;
            })),

            // These sendTo... property are mapped to the PO sending options section
            sendToRequester: pr.sendingOptions.requester,
            setSendToRequester: (sendToRequester) => set(produce((state) => {
                state.sendToRequester = sendToRequester;

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;

                return state;
            })),

            sendToSpender: pr.sendingOptions.spender,
            setSendToSpender: (sendToSpender) => set(produce((state) => {
                state.sendToSpender = sendToSpender;

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;

                return state;
            })),

            sendToBuyer: pr.sendingOptions.buyer,
            setSendToBuyer: (sendToBuyer) => set(produce((state) => {
                state.sendToBuyer = sendToBuyer;

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;

                return state;
            })),

            sendToOther: pr.sendingOptions.other,
            setSendToOther: (sendToOther) => set(produce((state) => {
                state.sendToOther = sendToOther;

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;

                return state;
            })),

            otherAddress: pr.sendingOptions.otherAddress,
            setOtherAddress: (otherAddress) => set(produce((state) => {
                state.otherAddress = otherAddress;

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;

                return state;
            })),

            p2pOther: pr.sendingOptions.p2pOther,
            setP2pOther: (value) => set((state) => {
                let mergedOtherAddresses;
                const otherAddressToArray = state.otherAddress.split(';');
                if (value) {
                    mergedOtherAddresses = mergeEmailsList(otherAddressToArray, state.p2pOtherSupplierAddresses);
                } else {
                    mergedOtherAddresses = deleteEmailsFromList(otherAddressToArray, state.p2pOtherSupplierAddresses);
                }

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    formatEmailList(mergedOtherAddresses),
                    state.sendToSupplier,
                    state.supplierAddresses);

                set({
                    p2pOther: value,
                    internalComment: updatedComment,
                });
            }),

            sendToSupplier: pr.sendingOptions.supplier,
            setSendToSupplier: (sendToSupplier) => set((state) => {
                let defaultSuppliers;
                let supplierAddresses;
                // when enabling, set the default supplier emails.
                // when disabling, clear all the fields
                if (sendToSupplier) {
                    const supplierKeys = ['p2pSupplier', 'vmdSupplier', 'pslSupplier'];
                    defaultSuppliers = PurchaseRequestController.setDefaultSupplierEmails(state);
                    state.p2pSupplier = defaultSuppliers.p2pSupplier;
                    state.vmdSupplier = defaultSuppliers.vmdSupplier;
                    state.pslSupplier = defaultSuppliers.pslSupplier;
                    supplierKeys.forEach((key) => {
                        state[key] = defaultSuppliers[key];
                    });
                    supplierAddresses = PurchaseRequestController.updateDefaultSupplierEmails(state);
                } else {
                    defaultSuppliers = {
                        p2pSupplier: false,
                        pslSupplier: false,
                        vmdSupplier: false,
                    };
                    supplierAddresses = [];
                }

                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    supplierAddresses);

                set({
                    sendToSupplier: sendToSupplier,
                    ...defaultSuppliers,
                    supplierAddresses: supplierAddresses,
                    internalComment: updatedComment,
                });
            }),

            supplierAddresses: pr.sendingOptions.supplierAddresses,
            setSupplierAddresses: (supplierAddresses) => set((state) => {
                state.supplierAddresses = supplierAddresses; // update to new value for addPOSendingEmailsInInternalCommentFromState method
                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);

                return {
                    supplierAddresses: supplierAddresses,
                    internalComment: updatedComment,
                };
            }),

            p2pSupplierAddresses: [],
            setP2pSupplierAddresses: (value) => set({p2pSupplierAddresses: value}),

            p2pOtherSupplierAddresses: [],
            setP2pOtherSupplierAddresses: (value) => set({p2pOtherSupplierAddresses: value}),

            p2pSupplier: pr.sendingOptions.p2pSupplier,
            setP2pSupplier: (value) => set((state) => {
                state.p2pSupplier = value; // update to new value for updateDefaultSupplierEmails method
                state.supplierAddresses = PurchaseRequestController.updateDefaultSupplierEmails(state);
                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;
            }),

            pslSupplier: pr.sendingOptions.pslSupplier,
            setPslSupplier: (value) => set((state) => {
                state.pslSupplier = value; // update to new value for updateDefaultSupplierEmails method
                state.supplierAddresses = PurchaseRequestController.updateDefaultSupplierEmails(state);
                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;
            }),

            vmdSupplierAddresses: [],
            setVmdSupplierAddresses: (value) => set({vmdSupplierAddresses: value}),

            vmdSupplier: pr.sendingOptions.vmdSupplier,
            setVmdSupplier: (value) => set((state) => {
                state.vmdSupplier = value; // update to new value for updateDefaultSupplierEmails method
                state.supplierAddresses = PurchaseRequestController.updateDefaultSupplierEmails(state);
                const updatedComment = PurchaseRequestController.addPOSendingEmailsInInternalCommentFromState(state.internalComment,
                    state.sendToSpender,
                    state.spender,
                    state.sendToRequester,
                    state.requester,
                    state.sendToBuyer,
                    state.sendToOther,
                    state.otherAddress,
                    state.sendToSupplier,
                    state.supplierAddresses);
                state.internalComment = updatedComment;
            }),

            hidePrice: pr.sendingOptions.hidePrice,
            setHidePrice: (newHidePrice) => set({hidePrice: newHidePrice}),

            coversheet: pr.sendingOptions.coversheet,
            setCoversheet: (newCoversheet) => set({coversheet: newCoversheet}),

            systemAlias: pr.systemAlias,
            setSystemAlias: (newSystemAlias) => set({systemAlias: newSystemAlias}),

            materialsReady: true,
            setMaterialsReady: (materialsReady) => set({materialsReady: materialsReady}),

            loadingMaterials: false,
            setLoadingMaterials: (loading) => set({loadingMaterials: loading}),

            materials: pr.materials.map((material) => ({
                ...material.getMaterial(),
                requestedDeliveryDate: getDateInCurrentTimezone(material.requestedDeliveryDate),
            })),
            setMaterials: (newMaterials, currentId) => set(produce((state) => {
                // Update materials
                state.materials = state.materials.map((material) => {
                    if (material.id === currentId) {
                        const currentMat = newMaterials.find((m) => m.id === currentId);

                        const dateInCurrentTimezone = currentMat.requestedDeliveryDate;
                        // If there is a delivery address and no storage location
                        const useDeliveryAddress = currentMat.deliveryAddress && currentMat.deliveryAddress?.manuallyModified === true;
                        const usePlantAddress = currentMat.plant.deliveryAddress && !currentMat.storageLocation?.value;
                        // If there is a storage location delivery address
                        const useStorageLocationAddress = !!currentMat.storageLocation.deliveryAddress && currentMat.storageLocation.deliveryAddress.value !== '';
                        let newDeliveryAddress;
                        if (useDeliveryAddress) {
                            newDeliveryAddress = currentMat.deliveryAddress;
                        } else if (usePlantAddress) {
                            newDeliveryAddress = currentMat.plant.deliveryAddress;
                        } else if (useStorageLocationAddress) {
                            newDeliveryAddress = currentMat.storageLocation.deliveryAddress;
                        } else {
                            // ass a bkp put what it's in deliveryAddress becasue sometimes plant is undefined
                            newDeliveryAddress = currentMat.deliveryAddress;
                        }
                        return {
                            ...currentMat,
                            requestedDeliveryDate: dateInCurrentTimezone,
                            deliveryAddress: newDeliveryAddress,
                        };
                    } else {
                        // All the materials of a PRs should have the same currency
                        return {
                            ...material,
                        };
                    }
                });

                // Replicate material's plant, storage loc, delivery address and requested delivery date in the header if needed
                setSameAddressToHeader(state, state.materials);
                return state;
            })),

            updateMaterials: (newMaterials) => set((state) => {
                state.materials = newMaterials.map((m) => m.getMaterial());
            }),

            deleteMaterial: (idToDelete) => set(produce((state) => {
                if (state.materials.length > 1) {
                    state.materials = state.materials.filter((material) => material.id !== idToDelete); // Delete the material

                    // Re-attribute the ID to have following numbers (0, 1, 2, ...)
                    if (!state.areAccountSplitByMaterial) {
                        for (let i = 0; i < state.materials.length; i++) {
                            state.materials[i].id = i;
                        }
                    } else { // If accounts are split by materials, we need to modify the material's ID in account as well
                        if (state.materials.length === 1) {
                            state.setAreAccountSplitByMaterial(false);
                            state.materials[0].id = 0;
                        } else {
                            state.accountDetailsByMaterial = state.accountDetailsByMaterial
                                .filter((a) => a.material_id !== idToDelete) // Delete the account details
                                .map((a, idx) => { // Update the IDs
                                    state.materials[idx].id = idx;
                                    return {
                                        ...a,
                                        material_id: idx,
                                    };
                                });
                        }
                    }
                }
            })),

            deleteMaterials: (idsToDelete) => set(produce((state) => {
                if (state.materials.length > 1) {
                    state.materials = state.materials.filter((material, index) => !idsToDelete.includes(index));
                    // Re-attribute the ID to have following numbers (0, 1, 2, ...)
                    if (!state.areAccountSplitByMaterial) {
                        for (let i = 0; i < state.materials.length; i++) {
                            state.materials[i].id = i;
                        }
                    } else { // If accounts are split by materials, we need to modify the material's ID in account as well
                        state.accountDetailsByMaterial = state.accountDetailsByMaterial
                            .filter((a) => !idsToDelete.includes(a.material_id))
                            .map((a, idx) => {
                                state.materials[idx].id = idx;
                                return {
                                    ...a,
                                    material_id: idx,
                                };
                            });
                    }
                }
            })),

            duplicateMaterial: (material) => set((state) => {
                // get the max Id from the state
                const maxMaterialId = state.materials.reduce((accumulator, current) => {
                    return accumulator.id > current.id ? accumulator : current;
                });
                const newId = maxMaterialId.id + 1;
                const newMaterial = new Material(
                    newId,
                    material.name,
                    material.description,
                    material.quantity,
                    material.unit,
                    material.baseUnit,
                    material.price,
                    material.currency,
                    material.per,
                    material.taxCode,
                    material.tax,
                    material.l1Category,
                    material.l3Category,
                    material.requestedDeliveryDate,
                    material.plant,
                    material.storageLocation,
                    material.deliveryAddress,
                    material.total,
                    material.freeItem,
                    material.transportCosts,
                    material.supplierPartId,
                    material.supplierPartAuxiliaryId,
                    material.arePriceDataFromST,
                    material.supplierComments,
                    material.internalComments,
                    material.exchangeCurrencyRate,
                    material.type,
                    material.supplierStatus,
                    material.pslId,
                    material.transportCostType,
                    material.transportCostConditions,
                    material.uniqueSourcingToolId,
                    material.ppmCostPlan,
                    material.taxAmount,
                    material.source,
                );

                set({
                    materials: [
                        ...state.materials,
                        {
                            ...newMaterial.getMaterial(),
                        },
                    ],
                    accountDetailsByMaterial: (state.areAccountSplitByMaterial ? [
                        ...state.accountDetailsByMaterial,
                        new AccountDetailsByMaterial(
                            newMaterial,
                            [new Account(
                                {label: '', value: ''},
                                {label: '', value: ''},
                                {label: '', value: ''},
                                {label: '', value: ''},
                                {label: '', value: ''},
                                {label: '', value: ''},
                                100,
                                'percentage',
                                '',
                                '',
                                false,
                            )],
                        ).getAccountDetailsByMaterial(),
                    ] : []),
                });
            }),
            updateAllMaterials: (newMaterials) => set(produce((state) => {
                state.materials = newMaterials.map((material) => {
                    return {
                        ...material,
                    };
                });

                const hasPSLMaterials = state.materials.some((material) => material.pslId && material.pslId !== '');
                const hasNonPSLMaterials = state.materials.some((material) => !material.pslId || material.pslId === '');

                if (hasPSLMaterials && hasNonPSLMaterials) {
                    state.prSource = determinePRSource(newMaterials.map((material) => material.source));

                    state.source = SOURCES.MIXED;
                }
            })),
            updateAllMaterialsFunctional: (updateFn) =>
                set((state) => {
                    const updatedMaterials = updateFn(state.materials);
                    state.materials = updatedMaterials.map((material) => ({...material}));
                }),
            deletedItemIds: [],
            setDeletedItem: (materialId) =>
                set(
                    produce((draft) => {
                        if (!draft.deletedItemIds.includes(materialId)) {
                            draft.deletedItemIds.push(materialId);
                        }
                    }),
                ),
            clearDeletedItems: () =>
                set(
                    produce((draft) => {
                        draft.deletedItemIds = [];
                    }),
                ),
            copiedItems: [],
            copiedItemIds: [],
            setCopiedItem: (material) =>
                set(
                    produce((draft) => {
                        const maxMaterialId = draft.materials.reduce((accumulator, current) => {
                            return accumulator.id > current.id ? accumulator : current;
                        });

                        const newId = maxMaterialId.id + 1;

                        const newMaterialInstance = new Material(
                            newId,
                            material.name,
                            material.description,
                            material.quantity,
                            material.unit,
                            material.baseUnit,
                            material.price,
                            material.currency,
                            material.per,
                            material.taxCode,
                            material.tax,
                            material.l1Category,
                            material.l3Category,
                            material.requestedDeliveryDate,
                            material.plant,
                            material.storageLocation,
                            material.deliveryAddress,
                            material.totalPrice,
                            material.freeItem,
                            material.transportCosts,
                            material.supplierPartId,
                            material.supplierPartAuxiliaryId,
                            material.arePriceDataFromST,
                            material.supplierComments,
                            material.internalComments,
                            material.exchangeCurrencyRate,
                            material.type,
                            material.supplierStatus,
                            material.pslId,
                            material.transportCostType,
                            material.transportCostConditions,
                            material.uniqueSourcingToolId,
                            material.ppmCostPlan,
                            material.taxAmount,
                            material.source,
                        );

                        const newMaterial = newMaterialInstance.getMaterial();

                        draft.copiedItemIds.push(newId);

                        draft.materials.push(newMaterial);

                        if (draft.areAccountSplitByMaterial) {
                            draft.accountDetailsByMaterial.push(
                                new AccountDetailsByMaterial(
                                    newMaterial,
                                    [
                                        new Account(
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            100,
                                            'percentage',
                                            '',
                                            '',
                                            false,
                                        ),
                                    ],
                                ).getAccountDetailsByMaterial(),
                            );
                        }
                    }),
                ),
            syncCopiedItems: () =>
                set(
                    produce((draft) => {
                        const copiedItems = draft.materials.filter((m) => draft.copiedItemIds.includes(m.id));

                        draft.copiedItems.push(...copiedItems);

                        draft.copiedItemIds = [];
                    }),
                ),
            appendCopiedItems: () =>
                set(
                    produce((draft) => {
                        if (draft.copiedItems.length === 0) return;

                        const maxId = draft.materials.reduce((max, item) => (item.id > max ? item.id : max), 0);

                        const newCopiedItems = draft.copiedItems.map((item, index) => ({
                            ...item,
                            id: maxId + index + 1,
                        }));

                        draft.materials.push(...newCopiedItems);
                    }),
                ),
            clearCopiedItems: () =>
                set(
                    produce((draft) => {
                        draft.copiedItems = [];
                        draft.copiedItemIds = [];
                    }),
                ),

            removeTransportCostsConditions: (cb) => set(produce((state)=>{
                state.materials.forEach((material) => {
                    material.transportCosts = false;
                    material.transportCostConditions = [];
                    material.transportCostType = TRANSPORT_OPTIONS_ENUM['No transport cost added'];
                });
                cb();
            })),
            removeTransportCostsConditionsForSelected: (selectedMaterialIds, cb) => set(produce((state)=>{
                state.materials.forEach((material) => {
                    if ( selectedMaterialIds.includes(material.id)) {
                        material.transportCostConditions = [];
                    }
                });
                // check if any other material has transport cost
                let doWeHaveAnyTransportCostConditions = false;
                state.materials.forEach((material)=> {
                    if (!doWeHaveAnyTransportCostConditions ) {
                        doWeHaveAnyTransportCostConditions = material.transportCostConditions.length > 0;
                    }
                });
                if (!doWeHaveAnyTransportCostConditions) {
                    state.materials.forEach((material) => {
                        material.transportCosts = false;
                        material.transportCostConditions = [];
                        material.transportCostType = TRANSPORT_OPTIONS_ENUM['No transport cost added'];
                    });
                }
                cb(!doWeHaveAnyTransportCostConditions);
            })),
            deleteConditionTypeForAllMaterials: (conditionTypeValue, selectedMaterialIds) => set(produce((state)=>{
                state.materials.forEach((material) =>{
                    if ( selectedMaterialIds.includes(material.id)) {
                        material.transportCostConditions = material.transportCostConditions.filter((condition)=> condition.conditionType.value !== conditionTypeValue);
                    }
                });
            })),
            addConditionTypeForAllMaterials: (receivedCondition, selectedMaterialIds) => set(produce((state)=>{
                state.materials.forEach((material) =>{
                    if ( selectedMaterialIds.includes(material.id)) {
                        // first check if the condition already exists
                        let conditionFound = false;
                        material.transportCostConditions.forEach((condition)=>{
                            if ( !conditionFound) {
                                conditionFound = (condition && condition.conditionType.value === receivedCondition.conditionType.valu);
                            }
                        });
                        if ( !conditionFound) {
                            material.transportCostConditions.push(receivedCondition);
                        }
                    }
                });
            })),
            addConditionTransportCost: (cb, selectedMaterialIds, materialId, transportCostConditions ) => set(produce((state)=>{
                // if materialId is set then we are just updating the material
                if ( materialId !== null && materialId !== undefined ) {
                    state.materials.forEach((material) =>{
                        if (material.id === materialId) {
                            material.transportCostConditions = [...transportCostConditions];
                        }
                    });
                } else if (selectedMaterialIds.length === 0) {
                    // we didn't select anything in the view so we are updating all the materials
                    state.materials.forEach((material) => {
                        material.transportCosts = true;
                        material.transportCostType = TRANSPORT_OPTIONS_ENUM['Via Condition type'];
                        material.transportCostConditions = [...transportCostConditions];
                    });
                } else if (selectedMaterialIds.length > 0) {
                    state.materials.forEach((material) => {
                        material.transportCosts = true;
                        material.transportCostType = TRANSPORT_OPTIONS_ENUM['Via Condition type'];
                        if ( selectedMaterialIds.includes(material.id)) {
                            material.transportCostConditions = [...transportCostConditions];
                        } else {
                            material.transportCostConditions = [];
                        }
                    });
                }
                cb();
            })),
            addTransportCosts: (cb, transportCostType) => set((state) => {
                if (PurchaseRequest.getTransportsCostsMaterial(state.materials) === null) {
                    // find the material with the highest total price
                    const referenceMaterial = state.materials.reduce((max, material) => Material.computeTotalPrice(max) > Material.computeTotalPrice(material) ? max : material);

                    MaterialController.getZSIMMaterialFromAnotherByL3($, referenceMaterial).then(
                        (m) => {
                            let materialName = referenceMaterial.name;
                            let materialType = referenceMaterial.type;
                            if (['UNBW', 'ZTAS', 'ZGNV'].includes(referenceMaterial.type) && m.length !== 0) {
                                materialName = `${m[0].matnr} - ${m[0].maktx}`;
                                materialType = m[0].mtart;
                            }

                            const material = new Material(
                                state.materials.length,
                                materialName,
                                'Transport costs',
                                '1',
                                referenceMaterial.unit,
                                referenceMaterial.baseUnit,
                                '',
                                referenceMaterial.currency,
                                '1',
                                referenceMaterial.taxCode,
                                '',
                                referenceMaterial.l1Category,
                                referenceMaterial.l3Category,
                                referenceMaterial.requestedDeliveryDate,
                                referenceMaterial.plant,
                                referenceMaterial.storageLocation,
                                referenceMaterial.deliveryAddress,
                                '',
                                false,
                                true,
                                '',
                                '',
                                false,
                                '',
                                '',
                                referenceMaterial.exchangeCurrencyRate,
                                materialType,
                                referenceMaterial.supplierStatus,
                                referenceMaterial.pslId,
                                transportCostType,
                                [],
                                referenceMaterial.uniqueSourcingToolId,
                                referenceMaterial.ppmCostPlan,
                                referenceMaterial.taxAmount,
                                referenceMaterial.source,
                            );

                            // convert to internal store format and push
                            set({
                                materials: [
                                    ...state.materials,
                                    {
                                        ...material.getMaterial(),
                                        requestedDeliveryDate: getDateInCurrentTimezone(material.requestedDeliveryDate),
                                    },
                                ],
                                accountDetailsByMaterial: (state.areAccountSplitByMaterial ? [
                                    ...state.accountDetailsByMaterial,
                                    new AccountDetailsByMaterial(
                                        material,
                                        [new Account(
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            {label: '', value: ''},
                                            100,
                                            'percentage',
                                            '',
                                            '',
                                            false,
                                        )],
                                    ).getAccountDetailsByMaterial(),
                                ] : []),
                            });
                            cb();
                        },
                    ).catch((reason) => console.error(reason));
                }
            }),
            getTotalMaterialPrice: (id) => PurchaseRequest.getTotalMaterialPrice(id, get().materials),
            getTotalMaterialPriceWithoutCurrencyExchange: (id) => PurchaseRequest.getTotalMaterialPriceWithoutCurrencyExchange(id, get().materials),
            getTotalMaterialQuantity: (id) => PurchaseRequest.getTotalMaterialQuantity(id, get().materials),
            getTotalPrice: () => PurchaseRequest.getTotalPrice(get().materials),
            getTotalPriceWithoutCurrencyExchange: () => PurchaseRequest.getTotalPriceWithoutCurrencyExchange(get().materials),
            getTotalPriceForBlankAndValidated: () => PurchaseRequest.getTotalPriceForBlankAndValidated(get().materials),
            getTotalConditionsPrice: () => get().materials.reduce((s, material) => s + getTotalValueOfConditions(material), 0),
            getTotalConditionPriceForMaterial: (material) => getTotalValueOfConditions(material),
            getTotalQuantity: () => PurchaseRequest.getTotalQuantity(get().materials),
            isThereAMissingTaxcode: () => get().materials.some((material) => isLabelValueEmpty(material.taxCode)),

            areAccountSplitByMaterial: pr.financialInfo.areAccountSplitByMaterial,
            setAreAccountSplitByMaterial: (theyAre) => set(produce((state) => {
                // Create either a new array of account details by material, or a new array of accounts, depends on if the user
                // choose to split the accounts by materials or not.
                const tempAccountDetailsByMaterial = [];
                state.materials.forEach((material) => {
                    if (material.id === 0) {
                        tempAccountDetailsByMaterial.push(new AccountDetailsByMaterial(
                            material,
                            state.accounts,
                        ).getFirstAccountDetailsByMaterial());
                    } else {
                        tempAccountDetailsByMaterial.push(new AccountDetailsByMaterial(
                            material,
                            [new Account(
                                {label: '', value: ''},
                                {label: '', value: ''},
                                {label: '', value: ''},
                                {label: '', value: ''},
                                {label: '', value: ''},
                                {label: '', value: ''},
                                100,
                                'percentage',
                                '',
                                '',
                                false,
                            ).getAccount()],
                        ).getFirstAccountDetailsByMaterial());
                    }
                });
                if (!state.accountDetailsByMaterial.length) {
                    state.accountDetailsByMaterial = tempAccountDetailsByMaterial;
                }
                state.areAccountSplitByMaterial = theyAre;
            })),

            accountDetailsByMaterial: pr.financialInfo.accountDetailsByMaterial.map((a) => ({
                material_id: a.material?.id,
                accounts: a.accounts.map((account) => account.getAccount()),
            })),
            setAccountDetailsByMaterial: (newAccountDetailsByMaterial, materialId) => set(produce((state) => {
                state.accountDetailsByMaterial = state.accountDetailsByMaterial.map((a) => {
                    const currentAccountDetailsByMaterial = newAccountDetailsByMaterial.find((item) => item.material_id === materialId);
                    if (a.material_id === materialId) {
                        return {
                            ...currentAccountDetailsByMaterial,
                        };
                    } else {
                        return a;
                    }
                });
                state.accounts = state.accountDetailsByMaterial[0].accounts;
                return state;
            })),
            deleteAccountDetailsByMaterial: (materialId, idxToDelete) => set((state) => {
                state.accountDetailsByMaterial = state.accountDetailsByMaterial.map((a) => {
                    if (a.material_id === materialId && a.accounts.length > 1) {
                        return {...a, accounts: a.accounts.filter((_, idx) => idx !== idxToDelete)};
                    } else {
                        return {...a};
                    }
                });
            }),
            cleanAccountDetailsByMaterial: () => set(produce((state) => {
                state.accountDetailsByMaterial = state.accountDetailsByMaterial.filter((a) => {
                    return a.material_id !== undefined;
                });
            })),
            addAccountDetailsByMaterials: (newEntries) => set((state) => {
                state.accountDetailsByMaterial = [...state.accountDetailsByMaterial, ...newEntries];
                state.accounts = state.accountDetailsByMaterial[0].accounts;
            }),
            accounts: pr.financialInfo.accounts.map((account) => account.getAccount()),
            setAccounts: (newAccounts, idx) => set(produce((state) => {
                // Update accounts
                state.accounts = state.accounts.map((account, accountIdx) => {
                    const currentAccount = newAccounts[idx];
                    if (accountIdx === idx) {
                        return {
                            ...currentAccount,
                        };
                    } else {
                        // All the accounts have the same account type
                        return {
                            ...account,
                            accountType: currentAccount.accountType,
                            allocationType: currentAccount.allocationType,
                            isFixed: currentAccount.isFixed,
                        };
                    }
                });
                if (!state.areAccountSplitByMaterial && state.accountDetailsByMaterial.length && state.accounts.length) {
                    state.accountDetailsByMaterial[0].accounts = state.accounts;
                }
            })),
            setAllAccounts: (newAccounts) => set({accounts: newAccounts}),
            createAccount: (materialId) => set((state) => {
                if (state.areAccountSplitByMaterial) {
                    state.accountDetailsByMaterial = state.accountDetailsByMaterial.map((item) => {
                        if (item.material_id === materialId) {
                            return {
                                ...item, accounts: [...item.accounts, new Account(
                                    {label: '', value: ''},
                                    {label: '', value: ''},
                                    item.accounts[0].accountType,
                                    {label: '', value: ''},
                                    {label: '', value: ''},
                                    {label: '', value: ''},
                                    100,
                                    item.accounts[0].allocationType,
                                    '',
                                    '',
                                    false,
                                ).getAccount()],
                            };
                        } else {
                            return item;
                        }
                    });
                } else {
                    state.accounts = [...state.accounts, new Account(
                        {label: '', value: ''},
                        {label: '', value: ''},
                        state.accounts[0].accountType,
                        {label: '', value: ''},
                        {label: '', value: ''},
                        {label: '', value: ''},
                        100,
                        state.accounts[0].allocationType,
                        '',
                        '',
                        false,
                    ).getAccount()];
                }
            }),
            deleteAccount: (idxToDelete) => set((state) => {
                if (state.accounts.length > 1) {
                    state.accounts = state.accounts.filter((_, idx) => idx !== idxToDelete);
                }
            }),
            setOneAccount: () => set(produce((state) => {
                state.accounts = state.accounts.slice(0, 1);
            })),
            mainAttachments: pr.attachmentsInfo.mainAttachments,
            setMainAttachments: (newAttachments) => (() => ({mainAttachments: newAttachments})),
            setMainAttachmentTopic: (newTopic, attachmentTag) => set(produce((state) => {
                state.mainAttachments = state.mainAttachments.map((att) => {
                    if (att.file === attachmentTag) {
                        att.topic = newTopic;
                    }
                    return att;
                });
            })),
            createMainAttachment: (newMainAttachments) => set(produce((state) => {
                state.mainAttachments = [...state.mainAttachments, newMainAttachments.getAttachment()];
            })),
            deleteMainAttachment: (idxToDelete) => set(produce((state) => {
                state.mainAttachments = state.mainAttachments.filter((_, idx) => idx !== idxToDelete);
            })),
            deleteMainAttachments: () => set(produce((state) => {
                state.mainAttachments = [];
            })),

            competitiveBiddingJustificationAttachments: pr.attachmentsInfo.competitiveBiddingJustificationAttachments,
            setCompetitiveBiddingJustificationAttachments: (newAttachments) => (() => ({competitiveBiddingJustificationAttachments: newAttachments})),
            setCBAttachmentTopic: (newTopic, attachmentTag) => set(produce((state) => {
                state.competitiveBiddingJustificationAttachments = state.competitiveBiddingJustificationAttachments.map((att) => {
                    if (att.file === attachmentTag) {
                        att.topic = newTopic;
                    }
                    return att;
                });

                return state;
            })),
            createCBAttachment: (newCBAttachment) => set(produce((state) => {
                state.competitiveBiddingJustificationAttachments = [...state.competitiveBiddingJustificationAttachments, newCBAttachment.getAttachment()];
                return state;
            })),
            deleteCBAttachment: (idxToDelete) => set(produce((state) => {
                state.competitiveBiddingJustificationAttachments = state.competitiveBiddingJustificationAttachments.filter((_, idx) => idx !== idxToDelete);
                return state;
            })),
            deleteAllCBAttachment: (idxToDelete) => set(produce((state) => {
                state.competitiveBiddingJustificationAttachments = state.competitiveBiddingJustificationAttachments.filter((_, idx) => idx === idxToDelete);
                return state;
            })),
            showCBJustification: false,
            setShowCBJustification: (newShowCBJustification) => set({showCBJustification: newShowCBJustification}),

            isTotalGreaterThanThreshold: true,
            setTotalGreaterThanThreshold: (newVal) => set({isTotalGreaterThanThreshold: newVal}),

            aribaID: pr.attachmentsInfo.aribaID,
            setAribaID: (newAribaID) => set({aribaID: newAribaID}),

            cbFormID: pr.attachmentsInfo.cbFormID,
            setCbFormID: (newCbFormID) => set({cbFormID: newCbFormID}),

            hasException: pr.attachmentsInfo.exception,
            setHasException: (newHasException) => set((state) => {
                state.selectedException = '';
                state.hasException = newHasException;
            }),

            selectedException: pr.attachmentsInfo.selectedException,
            setSelectedException: (newSelectedException) => set(produce((state) => {
                state.selectedException = newSelectedException;
                state.complianceIndicator = newSelectedException === 'Emergency case' ? complianceIndicatorOptions[1] : complianceIndicatorOptions[0];

                return state;
            })),

            complianceIndicator: pr.attachmentsInfo.complianceIndicator,
            setComplianceIndicator: (newComplianceIndicator) => set({complianceIndicator: newComplianceIndicator}),

            ers: pr.headerDetails.ers,
            setErs: (newValue) => set({ers: newValue}),

            grBasedInvoiceVerification: pr.headerDetails.grBasedInvoiceVerification,
            setGrBasedInvoiceVerification: (newValue) => set({grBasedInvoiceVerification: newValue}),

            goodsReceipt: pr.headerDetails.goodsReceipt,
            setGoodsReceipt: (newValue) => set({goodsReceipt: newValue}),

            supplierType: pr.oneTimeVendor.supplierType,
            setSupplierType: (newValue) => set({supplierType: newValue}),

            conversionFailed: '',
            setConversionFailed: (newValue) => set({conversionFailed: newValue}),

            companyCodeCurrency: '',
            setCompanyCodeCurrency: (currency) => set({companyCodeCurrency: currency}),

            cbThresholdCurrency: '',
            setCbThresholdCurrency: (currency) => set({cbThresholdCurrency: currency}),

            compCodeCurrToThresholdCurrExchangeRate: 1.0,
            setCompCodeCurrToThresholdCurrExchangeRate: (rate) => set({compCodeCurrToThresholdCurrExchangeRate: rate}),

            lastActiveUser: pr.lastActiveUser,
            setLastActiveUser: (newValue) => set({lastActiveUser: newValue}),

            userHasAccessToProperties: null,
            setUserHasAccessToProperties: (properties, pr) => set(produce((state) => {
                if (typeof properties === 'boolean') {
                    state.userHasAccessToProperties = properties;
                } else {
                    state.userHasAccessToProperties = PurchaseRequestController.userHasAccessToPrData(properties, pr);
                }
            })),

            prLocked: false,
            setPrLocked: (newValue) => set({prLocked: newValue}),

            sourcingEventID: pr.sourcingEventID,
            buyer: pr.buyer,

            actionButtonState: {
                loading: false,
                reason: '',
                step: 0,
                numberOfSteps: 10,
            },
            setActionButtonState: (newActionButtonState) => set((state) => {
                state.actionButtonState = newActionButtonState;

                return state;
            }),
            startActionButton: (reason, numberOfSteps) => set(produce((state) => {
                state.actionButtonState.loading = true;
                state.actionButtonState.reason = reason;
                state.actionButtonState.numberOfSteps = numberOfSteps;
                state.actionButtonState.step += 1;

                return state;
            })),
            stopActionButton: (reason, numberOfSteps) => set(produce((state) => {
                state.actionButtonState.loading = false;
                state.actionButtonState.reason = '';
                state.actionButtonState.step = 0;

                return state;
            })),
            nextStepActionButton: (nextReason) => set(produce((state) => {
                state.actionButtonState.reason = nextReason;
                if (state.actionButtonState.step + 1 <= state.actionButtonState.numberOfSteps) {
                    state.actionButtonState.step++;
                }
                return state;
            })),

            highlightMandatoryFieldForRequester: true,
            setHighlightMandatoryFieldForRequester: (checked) => set({highlightMandatoryFieldForRequester: checked}),

            highlightMandatoryFieldForSpender: true,
            setHighlightMandatoryFieldForSpender: (checked) => set({highlightMandatoryFieldForSpender: checked}),

            // MTV PowerBI validation
            mtvPowerBIValidated: false,
            setMtvPowerBIValidated: (validated) => set({mtvPowerBIValidated: validated}),

            // This function create a PurchaseRequest object, in order to submit it in the Neo4j database
            getPurchaseRequest: () => {
                const currentState = get();

                let filteredItems;

                if (currentState.source === SOURCES.CANDEX) {
                    filteredItems = currentState.materials.filter(
                        (m) => !currentState.deletedItemIds.includes(m.id),
                    );

                    filteredItems = [...filteredItems, ...currentState.copiedItems];

                    // Re-attribute IDs (0, 1, 2, ...)
                    filteredItems = filteredItems.map((mat, idx) => ({
                        ...mat,
                        id: idx,
                    }));
                } else {
                    filteredItems = currentState.materials;
                }

                return new PurchaseRequest(
                    currentState.vendorName,
                    currentState.vendorCode,
                    new OneTimeVendor(
                        currentState.OTVName,
                        currentState.OTVAddress,
                        currentState.OTVPhoneNumber,
                        currentState.supplierType,
                    ),
                    new NBSValidation(
                        currentState.operationalBuyers,
                        currentState.denialInfo,
                        currentState.operationalBuyersInDL,
                        currentState.obApprover,
                    ),
                    new SBValidation(
                        currentState.strategicBuyers,
                        currentState.sbDenialInfo,
                        currentState.sbApprover,
                    ),
                    currentState.creationDate.toDateString(),
                    currentState.prNumber,
                    currentState.projectTitle,
                    new WaysOfBuying(
                        currentState.wayOfBuying.currentValue,
                        currentState.wayOfBuying.availableOptions,
                    ),
                    currentState.spender,
                    currentState.requester,
                    currentState.rejectionMsg,
                    currentState.internalComment,
                    currentState.externalComment,
                    new HeaderDetails(
                        currentState.pslCompanyCodes,
                        currentState.companyCode,
                        currentState.purchaseOrganization,
                        currentState.purchasingGroup,
                        currentState.businessUnit,
                        currentState.plants,
                        currentState.defaultPlant,
                        currentState.defaultStorageLocation,
                        currentState.defaultDeliveryAddress,
                        currentState.wayOfBuying.currentValue.value !== 'FO' && currentState.defaultRequestedDeliveryDate ? new Date(currentState.defaultRequestedDeliveryDate).toDateString() : '',
                        currentState.wayOfBuying.currentValue.value === 'FO' && currentState.validityStart ? new Date(currentState.validityStart).toDateString() : '',
                        currentState.wayOfBuying.currentValue.value === 'FO' && currentState.validityEnd ? new Date(currentState.validityEnd).toDateString() : '',
                        currentState.paymentTerms,
                        currentState.newPaymentTerms,
                        currentState.orderingAddress,
                        currentState.invoicePartner,
                        currentState.incoterms,
                        currentState.supplierStatus,
                        currentState.ers,
                        currentState.grBasedInvoiceVerification,
                        currentState.goodsReceipt,
                    ),
                    new POSendingOptions(
                        currentState.sendToRequester,
                        currentState.sendToSpender,
                        currentState.sendToOther,
                        currentState.otherAddress,
                        currentState.sendToSupplier,
                        currentState.supplierAddresses,
                        currentState.p2pSupplier,
                        currentState.pslSupplier,
                        currentState.vmdSupplier,
                        currentState.hidePrice,
                        currentState.coversheet,
                        currentState.p2pOther,
                    ),
                    new FinancialInfo(
                        currentState.areAccountSplitByMaterial,
                        currentState.accountDetailsByMaterial.map((adbm) => new AccountDetailsByMaterial(new Material(adbm.material_id), adbm.accounts.map((a) => new Account(a.itemCategory, a.glNumber, a.accountType, a.wbs, a.costCenter, a.profitCenter, a.allocateFor, a.allocationType, a.accountStatus, a.consumptionLevel, a.isFixed)))),
                        currentState.accounts.map((a) => new Account(a.itemCategory, a.glNumber, a.accountType, a.wbs, a.costCenter, a.profitCenter, a.allocateFor, a.allocationType, a.accountStatus, a.consumptionLevel, a.isFixed)),
                    ),
                    filteredItems.map((m) => new Material(m.id, m.name, m.description, m.quantity, m.unit, m.baseUnit, m.price, m.currency, m.per, m.taxCode, '', m.l1Category, m.l3Category, m.requestedDeliveryDate instanceof Date ? m.requestedDeliveryDate.toDateString() : '', m.plant, m.storageLocation, m.deliveryAddress, '', m.freeItem, m.transportCosts, m.supplierPartId, m.supplierPartAuxiliaryId, m.arePriceDataFromST, m.supplierComments, m.internalComments, m.exchangeCurrencyRate, m.type, m.supplierStatus, m.pslId, m.transportCostType, m.transportCostConditions, m.uniqueSourcingToolId, m.ppmCostPlan, m.taxAmount, m.source)),
                    new AttachmentsInfo(currentState.mainAttachments.map((ma) => new Attachment(ma.file, ma.topic, ma.user, new Date(ma.date).toDateString(), ma.name, ma.userId)),
                        currentState.competitiveBiddingJustificationAttachments.map((cba) => new Attachment(cba.file, cba.topic, cba.user, new Date(cba.date).toDateString(), cba.name, cba.userId)),
                        undefined, '', currentState.hasException, currentState.selectedException, currentState.complianceIndicator),
                    currentState.status,
                    currentState.vendorContactEmails,
                    currentState.supplierLeadTime,
                    currentState.editableSupplier,
                    currentState.source,
                    currentState.contractID,
                    currentState.lastActiveUser,
                    currentState.sourcingEventID,
                    currentState.buyer,
                    currentState.submissionDate,
                    currentState.EPRId,
                    currentState.sourceFromApiKey,
                    currentState.nonPslJustification || '',
                    currentState.systemAlias,
                    currentState.sourceToDisplay,
                    currentState.prSource,
                );
            },
        })), {trace: true});
}

/**
 * @private
 * @param {*} state
 * @param {!Array<*>} newMaterials
 */
function setSameAddressToHeader(state, newMaterials) {
    const firstPlant = state.materials[0].plant;
    if (state.materials.length === 1 || newMaterials.every((item) => isLabelValueIdentical(firstPlant, item.plant))) {
        state.defaultPlant = firstPlant;
    }
    const firstStorageLocation = state.materials[0].storageLocation;
    if (state.materials.length === 1 || newMaterials.every((item) => isLabelValueIdentical(firstStorageLocation, item.storageLocation))) {
        state.defaultStorageLocation = firstStorageLocation;
    }
    const firstDeliveryAddress = state.materials[0].deliveryAddress;
    if (state.materials.length === 1 || newMaterials.every((item) => isAddressIdentical(firstDeliveryAddress, item.deliveryAddress))) {
        state.defaultDeliveryAddress = firstDeliveryAddress;
    }
    const firstRequestedDeliveryDate = state.materials[0].requestedDeliveryDate;
    if (state.materials.length === 1 || newMaterials.every((item) => firstRequestedDeliveryDate.toString() === item.requestedDeliveryDate.toString())) {
        state.defaultRequestedDeliveryDate = firstRequestedDeliveryDate;
    }
}
